import { useMemo } from 'react';
import { SourceType } from '../../shared/interfaces/source';
import { SelectSourceTypeCacheState } from './cache/interface';
import { useInMemoryFilteredList } from '../../shared/hooks/useInMemoryFilteredList';
import { SourceAPI } from '../api/SourceAPI';
import { SourceTypesFactory } from './factories/sourceTypesFactory';
import { SOURCE_TYPES_SEARCH_FILTER_KEY } from './constants';
import { matchPattern } from '../../shared/utils/string/pattern';
import { EntityList } from '../../entity-list/EntityList';
import { EntityTileProps } from '../../tiles/interfaces/tile';
import { ListComponentBaseProps } from '../../entity-list/interface';
import { TileGrid } from '../../tiles/TileGrid/TileGrid';
import { EntityTile } from '../../tiles/EntityTile/EntityTile';
import { SourceTilesFactory } from './factories/sourceTilesFactory';
import { EntityListEntities } from '../../shared/interfaces/entity-list';
import { ListConductorRawFilter } from '../../list-conductor/filters';
import { getSearchValue } from '../../shared/utils/filters';
import { UpgradePipelineBanner } from '../../pipelines/UpgradePipeline/UpgradePipelineBanner';
import { StandardPipelineInfo } from '../../shared/interfaces/integration';

export interface SelectSourceTypeProps {
  onSourceTypeSelect: (sourceType: SourceType) => void;
  initialData?: Partial<SelectSourceTypeCacheState>;
  onStateChange?: (state: SelectSourceTypeCacheState) => void;
  standardPipelineInfo?: StandardPipelineInfo;
  isUpgradingPipeline?: boolean;
}

export function SelectSourceType({
  onSourceTypeSelect,
  initialData,
  onStateChange,
  standardPipelineInfo,
  isUpgradingPipeline
}: SelectSourceTypeProps) {
  const { data, fetch } = useInMemoryFilteredList(
    async () => {
      const res = await SourceAPI.getSourceTypes();
      return SourceTypesFactory(res.data);
    },
    (item, params) => {
      return matchPattern(
        [item.displayName],
        getSearchValue(
          (params?.filters || []) as ListConductorRawFilter[],
          SOURCE_TYPES_SEARCH_FILTER_KEY
        )
      );
    },
    initialData?.sourceTypes
  );

  return (
    <>
      {isUpgradingPipeline ? (
        <UpgradePipelineBanner
          standardPipelineInfo={standardPipelineInfo}
          isObjectConfigPage={false}
        />
      ) : null}

      <EntityList
        entity={EntityListEntities.SOURCE_TYPE}
        mode='standalone'
        dataSource$={fetch}
        classes={
          isUpgradingPipeline
            ? {
                className: 'pt-0'
              }
            : undefined
        }
        listIcon='database'
        emptyTitle='No Matching Source Types Found'
        emptySubTitle='Modify your search filter term to get the desired result'
        showSearch
        searchKey={SOURCE_TYPES_SEARCH_FILTER_KEY}
        searchPlaceholder='Search Source Types'
        genericPageShimmer
        hidePagination
        hideRefresh
        newListConnectedTitle='No Source Type Available'
        genericErrorTitle='Error while fetching Source Types'
        genericErrorSubTitle='Retry Loading the Source Types'
        inMemoryList
        ListComponent={ListComponent}
        defaultListMeta={
          initialData?.filters
            ? {
                filters: initialData.filters,
                sort: {}
              }
            : undefined
        }
        onStateChange={state => {
          if (typeof onStateChange === 'function') {
            onStateChange({
              filters: state.filters,
              sourceTypes: data
            });
          }
        }}
        listComponentProps={{
          onSourceTypeSelect
        }}
      />
    </>
  );
}

function ListComponent({
  data,
  onSourceTypeSelect
}: { onSourceTypeSelect: (type: SourceType) => void } & ListComponentBaseProps<SourceType[]>) {
  const sourceTiles = useMemo(() => {
    return SourceTilesFactory(data || []);
  }, [data]);

  return (
    <TileGrid<SourceType, EntityTileProps<SourceType>>
      tiles={sourceTiles}
      onTileClick={tile => onSourceTypeSelect(tile.data)}
      TileComponent={EntityTile}
      className='w-100'
    />
  );
}
